import { useState, useCallback, useEffect, useMemo } from "react";
import { useModal } from "../../hooks/modalsHooks";
import Typography from "../Typography/Typography";
import ModalLayout from "../modals/ModalLayout";
import "./model.scss";
import { useNavigate } from "react-router-dom";
import Form from "../forms/Form";
import TextField from "../forms/TextField";
import {
    useInviteSCTUserMutation,
    useGetNodeUserDetailsQuery,
} from "../../services/userService";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { logEvent } from "@amplitude/analytics-browser";
import AccessPermissions from "../../pages/settings/users/AccessPermissions";
import Select from "../forms/Select";
import Checkbox from "../forms/Checkbox";

export default function InviteUserModal({
    tenantId,
    refetch,
    setLoading,
    existingUser = null,
    isResend = false,
}) {
    const { hideModal } = useModal();
    const navigate = useNavigate();
    const [inviteUser] = useInviteSCTUserMutation();

    // Component state
    const [isLoading, setIsLoading] = useState(false);
    const [formState, setFormState] = useState({
        selectedRole: existingUser?.role || "",
        selectedNodes: [],
        isActive: existingUser?.is_active !== false,
        initialNodes: {},
    });

    const { selectedRole, selectedNodes, isActive, initialNodes } = formState;

    const modalInfo = useMemo(
        () => ({
            title: existingUser ? "Update User" : "Invite User",
            submitButtonText: existingUser ? "Update User" : "Send Invitation",
            loadingText: existingUser ? "Updating..." : "Inviting...",
        }),
        [existingUser],
    );

    // Fetch detailed user data when editing an existing user
    const {
        data: userDetailsData,
        isSuccess: userDetailsSuccess,
        refetch: refetchUserDetails,
    } = useGetNodeUserDetailsQuery(existingUser?.id, {
        skip: !existingUser?.id,
    });

    // Refetch user details when modal opens with existing user
    useEffect(() => {
        if (existingUser?.id) {
            refetchUserDetails();
        }
    }, [existingUser, refetchUserDetails]);

    // Process user details when data is fetched
    useEffect(() => {
        if (userDetailsSuccess && userDetailsData) {
            const userData = userDetailsData.data;
            const updatedState = { ...formState };

            // Set role from detailed data
            if (userData.user?.role) {
                updatedState.selectedRole = userData.user.role;
            }

            // Set active status
            if (userData.user?.is_active !== undefined) {
                updatedState.isActive = userData.user.is_active;
            }

            // Process nodes data
            if (userData.projects?.length > 0) {
                const nodesList = [];
                const projectNodeMap = {};

                // Create a structured format for AccessPermissions component
                userData.projects.forEach((project) => {
                    if (project.nodes?.length > 0) {
                        project.nodes.forEach((node) => {
                            nodesList.push(node.id);
                            // Store project-node relationship for AccessPermissions
                            if (!projectNodeMap[project.project_id]) {
                                projectNodeMap[project.project_id] = [];
                            }
                            projectNodeMap[project.project_id].push(node.id);
                        });
                    }
                });

                updatedState.selectedNodes = nodesList;
                updatedState.initialNodes = projectNodeMap;
            }

            setFormState(updatedState);
        }
    }, [userDetailsSuccess, userDetailsData]);

    // Form validation schema
    const ValidationSchema = Yup.object().shape({
        email: Yup.string()
            .email("Please enter a valid email address")
            .max(255, "Email is too long")
            .required("Email is required")
            .trim("Email is required"),
        first_name: Yup.string()
            .max(100, "First name is too long")
            .required("First name is required")
            .trim("First name is required"),
        last_name: Yup.string()
            .max(100, "Last name is too long")
            .required("Last name is required")
            .trim("Last name is required"),
    });

    // Event handlers
    const handleClose = () => hideModal();

    const handleRoleChange = (role) => {
        setFormState((prev) => ({
            ...prev,
            selectedRole: role,
            // Clear selected nodes when switching to Admin role
            selectedNodes: role !== "SCT_NODE_USER" ? [] : prev.selectedNodes,
        }));
    };

    const handleNodesChange = useCallback((nodes) => {
        setFormState((prev) => ({
            ...prev,
            selectedNodes: nodes,
        }));
    }, []);

    const handleSubmit = async (values) => {
        setIsLoading(true);
        setLoading(true);

        try {
            const payload = {
                invitee_email: values.email,
                first_name: values.first_name,
                last_name: values.last_name,
                role: selectedRole,
                nodes_allocated:
                    selectedRole === "SCT_NODE_USER" ? selectedNodes : [],
                is_active: isActive,
            };

            // If this is a resend or edit, add appropriate fields
            if (isResend || existingUser) {
                payload.resend_email = isResend;
            }

            // If editing an existing user, include their ID
            if (existingUser) {
                payload.id = existingUser.id;
            }

            const inviteUserRes = await inviteUser(payload);

            if (inviteUserRes.data) {
                hideModal();

                const eventData = {
                    email: values.email,
                    role: selectedRole,
                };

                // Handle success based on operation type
                if (isResend) {
                    toast("Invitation has been resent");
                    logEvent({
                        eventName: "user_invitation_resent",
                        eventProperties: eventData,
                    });
                } else if (existingUser) {
                    toast("User updated successfully");
                    logEvent({
                        eventName: "user_updated_successfully",
                        eventProperties: eventData,
                    });
                } else {
                    toast("User Invited");
                    logEvent({
                        eventName: "user_invited_successfully",
                        eventProperties: eventData,
                    });
                }

                navigate(`/settings/users`);
                await refetch();
            } else {
                navigate(`/settings/users`);
                hideModal();
                const errorMessage =
                    inviteUserRes.error?.data?.errors?.[0] ||
                    "Failed to process user data. Please try again.";
                toast(errorMessage);
                logEvent({
                    eventName: existingUser
                        ? "user_update_failed"
                        : "user_invite_failed",
                    eventProperties: {
                        email: values.email,
                        errorMessage: errorMessage,
                    },
                });
            }
        } catch (error) {
            toast.error(`An error occurred: ${error.message}`);
        } finally {
            setIsLoading(false);
            setLoading(false);
        }
    };

    // Loading indicator while fetching user details
    if (existingUser?.id && !userDetailsSuccess && !userDetailsData) {
        return (
            <ModalLayout className="modal-dialog-centered mx-2" width={600}>
                <div className="card border-0 shadow-sm p-4">
                    <div className="text-center py-5">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                        >
                            <span className="visually-hidden">
                                Loading user data...
                            </span>
                        </div>
                        <p className="mt-3">Loading user details...</p>
                    </div>
                </div>
            </ModalLayout>
        );
    }

    return (
        <ModalLayout className="modal-dialog-centered mx-2" width={600}>
            <div className="card border-0 shadow-sm">
                <ModalLayout.Header>
                    <div className="w-100 px-4 pt-4 pb-3 border-bottom">
                        <Typography
                            variant="h2"
                            className="text-center fw-bold"
                        >
                            {modalInfo.title}
                        </Typography>
                    </div>
                </ModalLayout.Header>
                <ModalLayout.Body>
                    <div className="w-100 p-4">
                        <Form
                            initialValues={{
                                email: existingUser?.username || "",
                                first_name: existingUser?.first_name || "",
                                last_name: existingUser?.last_name || "",
                                isActive: isActive,
                                roleSelect: selectedRole,
                            }}
                            validationSchema={ValidationSchema}
                            onSubmit={handleSubmit}
                        >
                            <div className="mb-4">
                                <TextField
                                    required
                                    id="email"
                                    name="email"
                                    label="Email Address"
                                    placeholder="example@company.com"
                                    disabled={!!existingUser} // Disable email field when editing
                                />
                            </div>
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <TextField
                                        required
                                        id="first_name"
                                        name="first_name"
                                        label="First Name"
                                        placeholder="Enter first name"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <TextField
                                        required
                                        id="last_name"
                                        name="last_name"
                                        label="Last Name"
                                        placeholder="Enter last name"
                                    />
                                </div>
                            </div>
                            <div className="mb-4">
                                <Select
                                    name="roleSelect"
                                    label="User Role"
                                    required
                                    value={selectedRole}
                                    options={[
                                        {
                                            value: "SCT_TENANT_MANAGER",
                                            label: "Tenant Manager",
                                        },
                                        {
                                            value: "SCT_NODE_USER",
                                            label: "Node User",
                                        },
                                    ]}
                                    placeholder="Select a role for this user"
                                    onChange={(e) =>
                                        handleRoleChange(e.target.value)
                                    }
                                />
                                {selectedRole && (
                                    <small className="text-muted d-block mt-1">
                                        {selectedRole === "SCT_TENANT_MANAGER"
                                            ? "Tenant Manager has full access to all projects and settings"
                                            : "Node User can only access specified projects"}
                                    </small>
                                )}
                            </div>

                            {/* Active Status Toggle */}
                            {existingUser && (
                                <div className="mb-4">
                                    <Checkbox
                                        id="isActive"
                                        label="Active User"
                                        checked={isActive}
                                        name="isActive"
                                        onChange={(e) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                isActive: e.target.checked,
                                            }))
                                        }
                                    />
                                    <small className="text-muted d-block mt-1">
                                        Inactive users cannot log in to the
                                        system
                                    </small>
                                </div>
                            )}

                            {selectedRole === "SCT_NODE_USER" && (
                                <AccessPermissions
                                    onChange={handleNodesChange}
                                    initialSelectedNodes={initialNodes}
                                />
                            )}

                            <div className="mt-4 pt-3 d-flex justify-content-end border-top">
                                <button
                                    type="button"
                                    className="d-flex align-items-center btn btn-secondary"
                                    onClick={handleClose}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary ms-3 px-4"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            {modalInfo.loadingText}
                                        </>
                                    ) : (
                                        modalInfo.submitButtonText
                                    )}
                                </button>
                            </div>
                        </Form>
                    </div>
                </ModalLayout.Body>
            </div>
        </ModalLayout>
    );
}
